import { SerpAiBatchLaunch } from './SerpAiBatchLaunch';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  TextField,
} from '@mui/material';
import { Box, Stack, Typography } from './mui-system';
import { useCanEdit } from './EditorOnly';
import { api } from '../api/client';
import type { FakesResult } from '../api/types';
import {
  canAcceptAiProposal,
  type AiBatch,
  type AiSelectionScope,
  AI_CATEGORIES,
  AI_CATEGORY_LABELS,
  type AiCategory,
  type AiProposal,
  type AiProposalPage,
  type AiSettings,
} from '../../../api/src/fakes/serp-ai.types';
import { MENTION_LABELS, type MentionSentiment } from '../../../api/src/fakes/serp-classification';
import { fakeDate, fakeError } from './fakes-presentation';
const statusLabels: Record<AiProposal['status'], string> = {
  preparing: 'Готовим страницу',
  processing: 'Модель анализирует',
  ready: 'Предложение готово',
  failed: 'Анализ не выполнен',
  uncertain: 'Исход вызова неизвестен',
};
const reviewLabels = {
  accepted: 'Принято',
  corrected: 'Исправлено и принято',
  rejected: 'Отклонено',
};
const dollars = (v: number) => '$' + v.toFixed(v < 1 ? 4 : 2);
export function SerpAiDialog({
  workspaceId,
  candidates,
  scope,
  resultId,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  candidates: FakesResult[];
  scope?: AiSelectionScope;
  resultId?: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const editable = useCanEdit('fakes.run');
  const canConfigure = useCanEdit('fakes.settings');
  const canReview = useCanEdit('fakes.edit');
  const canReviewCompetitor = useCanEdit('fakes.edit', 'registry.create', 'registry.edit');
  const canStop = useCanEdit('fakes.stop');
  const choices = useMemo(() => {
    const byKey = new Map<string, FakesResult>();
    for (const r of [...candidates].sort((a, b) => a.rank - b.rank))
      if (
        ['unknown', 'unreviewed'].includes(r.classification) &&
        !byKey.has(r.resourceKey ?? r.url)
      )
        byKey.set(r.resourceKey ?? r.url, r);
    return [...byKey.values()];
  }, [candidates]);
  const [selection, setSelected] = useState<string[]>(() => choices.slice(0, 40).map((r) => r.id));
  const selected = useMemo(() => {
    const eligible = new Set(choices.map((r) => r.id));
    return selection.filter((id) => eligible.has(id));
  }, [selection, choices]);
  const [planVersion, setPlanVersion] = useState(0);
  const [batches, setBatches] = useState<AiBatch[]>([]);
  const [batchId, setBatchId] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<AiCategory | ''>('');
  const [pendingOnly, setPendingOnly] = useState(!resultId);
  const [reviewSelection, setReviewSelection] = useState<string[]>([]);
  const [bulkSkipped, setBulkSkipped] = useState<
    Array<{ id: string; reason: string; url: string }>
  >([]);
  const [settings, setSettings] = useState<AiSettings | null>(null),
    [data, setData] = useState<AiProposalPage | null>(null);
  const [brief, setBrief] = useState(''),
    [briefRevision, setBriefRevision] = useState(-1);
  const initialized = useRef(false),
    alive = useRef(true),
    mutating = useRef(false);
  const [page, setPage] = useState(1),
    [version, setVersion] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('');
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    let cancelled = false,
      timer: ReturnType<typeof setTimeout>;
    async function read() {
      try {
        const [next, rows, runs] = await Promise.all([
          api.serpAiSettings(workspaceId),
          api.serpAiProposals(workspaceId, {
            resultId,
            page,
            batchId: batchId || undefined,
            pendingOnly,
            category: categoryFilter || undefined,
          }),
          resultId ? Promise.resolve([]) : api.serpAiBatches(workspaceId),
        ]);
        if (cancelled) return;
        setSettings(next);
        setData(rows);
        setBatches(runs);
        if (!initialized.current) {
          initialized.current = true;
          setBrief(next.profile.brief);
          setBriefRevision(next.profile.revision);
        }
        if (
          rows.items.some((r) => ['preparing', 'processing'].includes(r.status)) ||
          runs.some((r) => r.status === 'queued' || r.preparing > 0 || r.processing > 0) ||
          rows.active
        )
          timer = setTimeout(() => void read(), 3000);
      } catch (e) {
        if (!cancelled) setError(fakeError(e));
      }
    }
    void read();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [workspaceId, resultId, page, version, batchId, pendingOnly, categoryFilter]);
  async function refreshMap() {
    await onSaved();
    if (alive.current) setPlanVersion((v) => v + 1);
  }
  async function act(work: () => Promise<void>) {
    if (mutating.current) return;
    mutating.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await work();
    } catch (e) {
      if (alive.current) setError(fakeError(e));
    } finally {
      if (alive.current) setVersion((v) => v + 1);
      mutating.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const dirty = Boolean(
    settings &&
    (brief.trim() !== settings.profile.brief || briefRevision !== settings.profile.revision),
  );
  const launchable = Boolean(settings?.enabled && settings.profile.revision && !dirty);
  async function launch(ids: string[], retryId?: string) {
    if (!settings) return;
    const started = await api.startSerpAi(workspaceId, {
      resultIds: ids,
      profileRevision: settings.profile.revision,
      retryId,
    });
    if (alive.current) {
      setPage(1);
      setSelected([]);
      setMessage(
        `В обработке или уже сохранено: ${started.ids.length}. Пропущено подтверждённых: ${started.skipped.length}.`,
      );
    }
  }
  const reviewable = (data?.items ?? []).filter(
    (row) =>
      canAcceptAiProposal(row) &&
      (row.suggestion?.category !== 'competitor' || canReviewCompetitor),
  );
  const selectedReviews = reviewable.filter((r) => reviewSelection.includes(r.id));
  function changeFilter(update: () => void) {
    update();
    setPage(1);
    setData(null);
    setReviewSelection([]);
    setBulkSkipped([]);
  }
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={() => !busy && onClose()}
      aria-labelledby="serp-ai-title"
    >
      <DialogTitle id="serp-ai-title">ИИ-разбор выдачи</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography>
            Модель предлагает категорию и цитаты. Цвет карты меняется после вашего подтверждения.
          </Typography>
          {!settings && <LinearProgress aria-label="Чтение настроек ИИ" />}
          {settings && (
            <>
              {!settings.enabled && (
                <Alert severity="info">
                  ИИ не подключён. Администратору нужно добавить серверный ключ
                  SERP_AI_OPENAI_API_KEY. Сохранённые предложения доступны для просмотра.
                </Alert>
              )}
              <Typography fontSize={12}>
                {settings.model} · Осталось {dollars(settings.budget.remainingUsd)} из{' '}
                {dollars(settings.budget.limitUsd)} общего бюджета. Учтено:{' '}
                {dollars(settings.budget.accountedUsd)}; резерв:{' '}
                {dollars(settings.budget.reservedUsd)}; ожидает подтверждения расхода:{' '}
                {dollars(settings.budget.uncertainUsd)}.
              </Typography>
              <Box component="details" open={!settings.profile.brief}>
                <Box component="summary" sx={{ cursor: 'pointer' }}>
                  Описание бренда и правила конкуренции
                </Box>
                <Stack gap={1} mt={1}>
                  <TextField
                    label="Правила анализа"
                    multiline
                    minRows={4}
                    value={brief}
                    disabled={!canConfigure || busy}
                    slotProps={{ htmlInput: { maxLength: 4000 } }}
                    onChange={(e) => setBrief(e.target.value)}
                    helperText="Укажите названия бренда, услуги и кого считать конкурентами. Например, для Ashoo включите обычные сайты знакомств."
                  />
                  {briefRevision !== settings.profile.revision && (
                    <Alert severity="warning">
                      Правила изменились у другого редактора. Перечитайте их перед сохранением.
                    </Alert>
                  )}
                  {canConfigure && (
                    <Stack direction="row" gap={1}>
                      <Button
                        disabled={busy || !dirty || brief.trim().length < 20}
                        onClick={() =>
                          void act(async () => {
                            const saved = await api.saveSerpAiProfile(workspaceId, {
                              brief,
                              expectedRevision: briefRevision,
                            });
                            if (alive.current) {
                              setSettings(saved);
                              setBrief(saved.profile.brief);
                              setBriefRevision(saved.profile.revision);
                            }
                          })
                        }
                      >
                        Сохранить правила
                      </Button>
                      <Button
                        disabled={busy}
                        onClick={() => {
                          setBrief(settings.profile.brief);
                          setBriefRevision(settings.profile.revision);
                        }}
                      >
                        Перечитать правила
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Box>
              {editable && (
                <>
                  {scope && (
                    <SerpAiBatchLaunch
                      workspaceId={workspaceId}
                      scope={scope}
                      refreshKey={planVersion}
                      busy={busy}
                      launchable={launchable}
                      revision={settings.profile.revision}
                      remainingUsd={settings.budget.remainingUsd}
                      act={act}
                      onStarted={(id) => {
                        if (!alive.current) return;
                        changeFilter(() => setBatchId(id));
                        setMessage('Разбор всей выдачи запущен. Окно можно закрыть.');
                      }}
                    />
                  )}
                  <Box component="details" open={!scope}>
                    <Box component="summary" sx={{ cursor: 'pointer', mb: 1 }}>
                      {scope ? 'Выбрать отдельные URL на текущей странице' : 'Выбор URL'}
                    </Box>
                    <Typography fontWeight={600}>
                      {resultId
                        ? 'Анализ этого URL'
                        : 'Выберите до 40 URL из отображаемой части карты'}
                    </Typography>
                    {!choices.length && (
                      <Typography>В выбранной выдаче нет неразобранных ресурсов.</Typography>
                    )}
                    <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                      {choices.map((r) => (
                        <FormControlLabel
                          key={r.id}
                          sx={{ display: 'flex', alignItems: 'start', overflowWrap: 'anywhere' }}
                          control={
                            <Checkbox
                              checked={selected.includes(r.id)}
                              disabled={busy || (!selected.includes(r.id) && selected.length >= 40)}
                              onChange={(_, checked) =>
                                setSelected((old) =>
                                  checked ? [...old, r.id] : old.filter((id) => id !== r.id),
                                )
                              }
                            />
                          }
                          label={r.url}
                        />
                      ))}
                    </Box>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      <Button
                        disabled={busy || !choices.length}
                        onClick={() => setSelected(choices.slice(0, 40).map((r) => r.id))}
                      >
                        Выбрать первые 40
                      </Button>
                      <Button disabled={busy || !selected.length} onClick={() => setSelected([])}>
                        Снять выбор
                      </Button>
                      <Button
                        variant="contained"
                        disabled={busy || !launchable || !selected.length}
                        onClick={() => void act(() => launch(selected))}
                      >
                        Разобрать с ИИ ({selected.length})
                      </Button>
                    </Stack>
                    {selected.length > 0 && (
                      <Typography fontSize={12}>
                        Перед запуском резервируется до{' '}
                        {dollars(selected.length * settings.budget.maxPerUrlUsd)}. Будет передан
                        текст выбранных страниц в OpenAI. Недоступные страницы не отправляются
                        модели.
                      </Typography>
                    )}
                  </Box>
                  {dirty && (
                    <Alert severity="info">
                      Сохраните правила или перечитайте сохранённую версию перед запуском.
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
          {busy && <LinearProgress aria-label="Сохранение ИИ-разбора" />}
          {error && <Alert severity="error">{error}</Alert>}
          {message && <Alert severity="success">{message}</Alert>}
          {!resultId && batches.length > 0 && (
            <Stack gap={1}>
              <Typography fontWeight={700}>Массовые запуски</Typography>
              {batches.slice(0, 3).map((batch) => (
                <Box
                  key={batch.id}
                  sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  <Typography fontWeight={600}>{batch.label}</Typography>
                  <Typography fontSize={12}>
                    {
                      {
                        queued: 'Выполняется',
                        complete: 'Завершён',
                        stopped: 'Остановлен',
                        failed: 'Прерван',
                      }[batch.status]
                    }{' '}
                    · обработано {batch.submitted} из {batch.total} URL · готово {batch.ready} ·
                    подтверждено {batch.reviewed} · пропущено {batch.skipped} · ошибок{' '}
                    {batch.failed}
                  </Typography>
                  {(batch.status === 'queued' || batch.preparing + batch.processing > 0) && (
                    <LinearProgress aria-label="Разбор всей выдачи" />
                  )}
                  {batch.error && <Alert severity="warning">{batch.error}</Alert>}
                  <Stack direction="row" gap={1}>
                    <Button
                      disabled={busy}
                      onClick={() =>
                        changeFilter(() => {
                          setBatchId(batch.id);
                          setPendingOnly(false);
                        })
                      }
                    >
                      Предложения запуска
                    </Button>
                    {canStop &&
                      (batch.status === 'queued' ||
                        batch.status === 'failed' ||
                        batch.preparing + batch.processing > 0) && (
                        <Button
                          disabled={busy}
                          onClick={() =>
                            void act(async () => {
                              await api.stopSerpAiBatch(workspaceId, batch.id);
                              if (alive.current)
                                setMessage(
                                  'Запуск остановлен. Уже отправленные модели запросы могут завершиться.',
                                );
                            })
                          }
                        >
                          Остановить
                        </Button>
                      )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={700}>Предложения и история</Typography>
            <Button
              disabled={busy}
              onClick={() => {
                setError('');
                setVersion((v) => v + 1);
              }}
            >
              Обновить список
            </Button>
          </Stack>
          {!resultId && (
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <TextField
                select
                label="Категория предложений"
                value={categoryFilter}
                disabled={busy}
                sx={{ minWidth: 180 }}
                onChange={(e) =>
                  changeFilter(() => setCategoryFilter(e.target.value as AiCategory | ''))
                }
              >
                <MenuItem value="">Все категории</MenuItem>
                {AI_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {AI_CATEGORY_LABELS[c]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Запуск"
                value={batchId}
                disabled={busy}
                sx={{ minWidth: 180 }}
                onChange={(e) => changeFilter(() => setBatchId(e.target.value))}
              >
                <MenuItem value="">Все предложения</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.label} · {fakeDate(b.createdAt)}
                  </MenuItem>
                ))}
                {batchId && !batches.some((b) => b.id === batchId) && (
                  <MenuItem value={batchId}>Выбранный запуск</MenuItem>
                )}
              </TextField>
              <FormControlLabel
                label="Только ожидающие проверки"
                control={
                  <Checkbox
                    checked={pendingOnly}
                    disabled={busy}
                    onChange={(_, checked) => changeFilter(() => setPendingOnly(checked))}
                  />
                }
              ></FormControlLabel>
            </Stack>
          )}
          {canReview && !resultId && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', py: 1 }}
            >
              <Button
                disabled={busy || !reviewable.length}
                onClick={() => setReviewSelection(reviewable.map((r) => r.id))}
              >
                Выбрать готовые на странице ({reviewable.length})
              </Button>
              <Button
                disabled={busy || !selectedReviews.length}
                onClick={() => setReviewSelection([])}
              >
                Снять выбор предложений
              </Button>
              <Button
                variant="contained"
                disabled={busy || !selectedReviews.length}
                onClick={() =>
                  void act(async () => {
                    let result;
                    try {
                      result = await api.acceptSerpAi(
                        workspaceId,
                        selectedReviews.map((r) => r.id),
                      );
                    } finally {
                      // A transport failure may follow a partial commit: refresh the map as well as history.
                      await refreshMap();
                    }
                    if (alive.current) {
                      setReviewSelection([]);
                      setData(null);
                      if (pendingOnly) setPage(1);
                      setBulkSkipped(
                        result.skipped.map((item) => ({
                          ...item,
                          url: selectedReviews.find((r) => r.id === item.id)?.url ?? item.id,
                        })),
                      );
                      setMessage(
                        `Принято: ${result.accepted.length}. Пропущено: ${result.skipped.length}.`,
                      );
                    }
                  })
                }
              >
                Принять выбранные ({selectedReviews.length})
              </Button>
            </Stack>
          )}
          {bulkSkipped.length > 0 && (
            <Alert severity="warning">
              {bulkSkipped.map((item) => (
                <Typography key={item.id} sx={{ overflowWrap: 'anywhere' }}>
                  {item.url}: {item.reason}
                </Typography>
              ))}
            </Alert>
          )}
          {!data && <LinearProgress aria-label="Чтение предложений" />}
          {data && !data.items.length && (
            <Typography>Нет предложений по выбранным условиям.</Typography>
          )}
          {data?.items.map((row) => (
            <Proposal
              key={row.id}
              row={row}
              selectable={canReview && !resultId && reviewable.some((item) => item.id === row.id)}
              checked={selectedReviews.some((r) => r.id === row.id)}
              onChecked={(checked) =>
                setReviewSelection((old) =>
                  checked ? [...old, row.id] : old.filter((id) => id !== row.id),
                )
              }
              editable={canReview}
              busy={busy}
              canRetry={launchable}
              review={(input) =>
                act(async () => {
                  await api.reviewSerpAi(workspaceId, row.id, input);
                  await refreshMap();
                })
              }
              retry={() => act(() => launch([row.resultId], row.id))}
            />
          ))}
          {data && (
            <Stack direction="row" gap={1} alignItems="center">
              <Button
                disabled={busy || page === 1}
                onClick={() => {
                  setData(null);
                  setReviewSelection([]);
                  setPage((p) => p - 1);
                }}
              >
                Назад
              </Button>
              <Typography>Страница {page}</Typography>
              <Button
                disabled={busy || !data.hasMore}
                onClick={() => {
                  setData(null);
                  setReviewSelection([]);
                  setPage((p) => p + 1);
                }}
              >
                Далее
              </Button>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Закрыть ИИ-разбор
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function Proposal({
  row,
  selectable,
  checked,
  onChecked,
  editable,
  busy,
  canRetry,
  review,
  retry,
}: {
  row: AiProposal;
  selectable: boolean;
  checked: boolean;
  onChecked: (checked: boolean) => void;
  editable: boolean;
  busy: boolean;
  canRetry: boolean;
  review: (input: Parameters<typeof api.reviewSerpAi>[2]) => Promise<void>;
  retry: () => Promise<void>;
}) {
  const canRegistry = useCanEdit('registry.create', 'registry.edit');
  const canRun = useCanEdit('fakes.run');
  const [editing, setEditing] = useState(false),
    [category, setCategory] = useState<AiCategory>(row.suggestion?.category ?? 'unreviewed'),
    [sentiment, setSentiment] = useState<MentionSentiment | ''>(row.suggestion?.sentiment ?? ''),
    [reason, setReason] = useState(row.suggestion?.reason ?? '');
  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Stack gap={1}>
        <Stack direction="row" alignItems="start" gap={1}>
          {selectable && (
            <Checkbox
              checked={checked}
              disabled={busy}
              onChange={(_, value) => onChecked(value)}
              slotProps={{ input: { 'aria-label': `Выбрать предложение: ${row.url}` } }}
              sx={{ p: 0.5 }}
            />
          )}
          <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
            {row.url}
          </Typography>
        </Stack>
        <Typography fontSize={12}>
          {row.review ? reviewLabels[row.review.action] : statusLabels[row.status]} ·{' '}
          {fakeDate(row.createdAt)} · {row.model}
        </Typography>
        {['preparing', 'processing'].includes(row.status) && (
          <LinearProgress aria-label="Анализ страницы" />
        )}
        {row.error && <Alert severity={row.costUncertain ? 'warning' : 'error'}>{row.error}</Alert>}
        {row.stale && (
          <Alert severity="warning">Предложение устарело: {row.stale}. Принятие недоступно.</Alert>
        )}
        {row.suggestion && (
          <>
            <Typography fontWeight={600}>
              {AI_CATEGORY_LABELS[row.suggestion.category]}
              {row.suggestion.sentiment ? ' · ' + MENTION_LABELS[row.suggestion.sentiment] : ''}
            </Typography>
            <Typography>{row.suggestion.reason}</Typography>
            {row.suggestion.quotes.map((q, i) => (
              <Typography
                component="blockquote"
                key={i}
                sx={{
                  m: 0,
                  pl: 2,
                  borderLeft: '2px solid',
                  borderColor: 'divider',
                  overflowWrap: 'anywhere',
                }}
              >
                «{q.text}»
              </Typography>
            ))}
          </>
        )}
        {row.evidence && (
          <Box component="details">
            <Box component="summary" sx={{ cursor: 'pointer' }}>
              Текст, переданный модели
            </Box>
            <Typography fontSize={12} sx={{ overflowWrap: 'anywhere' }}>
              Адрес ответа: {row.evidence.finalUrl}
            </Typography>
            {row.evidence.page.textTruncated && (
              <Typography fontSize={12}>Модель получила сокращённый текст.</Typography>
            )}
            <Typography
              component="pre"
              fontSize={12}
              sx={{
                whiteSpace: 'pre-wrap',
                maxHeight: 250,
                overflow: 'auto',
                overflowWrap: 'anywhere',
              }}
            >
              {[
                row.evidence.page.title,
                row.evidence.page.h1,
                row.evidence.page.description,
                row.evidence.page.text,
              ]
                .filter(Boolean)
                .join('\n\n')}
            </Typography>
          </Box>
        )}
        <Typography fontSize={12} color="text.secondary">
          {row.status === 'preparing' || row.status === 'processing'
            ? 'Резерв'
            : row.costUncertain
              ? 'Сохранённый резерв'
              : 'Оценка расхода'}
          : {dollars(row.costUsd)}
          {row.usage
            ? ` · токены: ${row.usage.inputTokens} вход / ${row.usage.outputTokens} выход`
            : ''}
        </Typography>
        {row.review && (
          <Typography fontSize={12}>
            {reviewLabels[row.review.action]} {fakeDate(row.review.at)}
            {row.review.category ? ' · ' + AI_CATEGORY_LABELS[row.review.category] : ''}
            {row.review.sentiment ? ' · ' + MENTION_LABELS[row.review.sentiment] : ''}:{' '}
            {row.review.reason}
          </Typography>
        )}
        {editable && !row.review && row.status === 'ready' && row.suggestion && (
          <>
            {editing && (
              <Stack gap={1}>
                <TextField
                  select
                  label="Категория после исправления"
                  value={category}
                  disabled={busy}
                  onChange={(e) => {
                    setCategory(e.target.value as AiCategory);
                    if (e.target.value !== 'about') setSentiment('');
                  }}
                >
                  {AI_CATEGORIES.map((k) => (
                    <MenuItem key={k} value={k}>
                      {AI_CATEGORY_LABELS[k]}
                    </MenuItem>
                  ))}
                </TextField>
                {category === 'about' && (
                  <TextField
                    select
                    label="Контекст упоминания"
                    value={sentiment}
                    disabled={busy}
                    onChange={(e) => setSentiment(e.target.value as MentionSentiment | '')}
                  >
                    <MenuItem value="">Не оценён</MenuItem>
                    {Object.entries(MENTION_LABELS).map(([k, v]) => (
                      <MenuItem key={k} value={k}>
                        {v}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  label="Основание исправления"
                  multiline
                  minRows={2}
                  value={reason}
                  disabled={busy}
                  slotProps={{ htmlInput: { maxLength: 1900 } }}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Stack>
            )}
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                disabled={
                  busy ||
                  Boolean(row.stale) ||
                  (editing && !reason.trim()) ||
                  (!canRegistry && (editing ? category : row.suggestion?.category) === 'competitor')
                }
                onClick={() =>
                  void review(
                    editing
                      ? {
                          action: 'correct',
                          category,
                          sentiment: category === 'about' ? sentiment || null : null,
                          reason,
                        }
                      : { action: 'accept' },
                  )
                }
              >
                {editing ? 'Сохранить исправление' : 'Принять'}
              </Button>
              <Button
                disabled={busy || Boolean(row.stale)}
                onClick={() => {
                  if (!editing && row.suggestion) {
                    setCategory(row.suggestion.category);
                    setSentiment(row.suggestion.sentiment ?? '');
                    setReason(row.suggestion.reason);
                  }
                  setEditing(!editing);
                }}
              >
                {editing ? 'Отменить исправление' : 'Исправить'}
              </Button>
              <Button
                disabled={busy}
                onClick={() => void review({ action: 'reject', reason: 'Отклонено редактором' })}
              >
                Отклонить
              </Button>
            </Stack>
          </>
        )}
        {canRun && !row.review && (['failed', 'uncertain'].includes(row.status) || row.stale) && (
          <Button disabled={busy || !canRetry} onClick={() => void retry()}>
            Запустить новый анализ
          </Button>
        )}
      </Stack>
    </Box>
  );
}
